import { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { ERoleType } from 'enum';
import { enumToLabelValueList, getErrorMessage, toTitleCase } from 'common/utils';
import { useUpdateUserRole } from 'hooks/useUsers';
import CustomDialog from 'components/dialogs/CustomDialog';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { snackbarService } from 'services/snackbarService';

interface Props {
  userId: string;
  role: ERoleType;
  name: string;
}

export default function ChangeUserRole({ userId, role, name }: Props) {
  const [open, setOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<ERoleType | null>(null);

  const [currentRole, setCurrentRole] = useState<ERoleType>(role);

  const roles = useMemo(() => enumToLabelValueList(ERoleType), []);

  useEffect(() => {
    setCurrentRole(role);
  }, [role]);

  const { mutate, isLoading } = useUpdateUserRole(
    () => {
      if (pendingRole) setCurrentRole(pendingRole);
      setPendingRole(null);
      setOpen(false);
      snackbarService.showSnackbar('Role has been changed successfully', 'success');
    },
    (error: any) => {
      setPendingRole(null);
      setOpen(false);
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
    },
  );

  const onSelectChange = (e: SelectChangeEvent) => {
    const next = e.target.value as ERoleType;

    if (next === currentRole) return;

    setPendingRole(next);
    setOpen(true);
  };

  const confirmChangeRole = () => {
    if (!pendingRole) return;

    mutate({
      userId,
      role: pendingRole,
    });
  };

  const cancelChangeRole = () => {
    setPendingRole(null);
    setOpen(false);
  };

  return (
    <>
      <Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={currentRole}
            onChange={onSelectChange}
            disabled={isLoading}
          >
            {roles.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <CustomDialog
        open={open}
        title="Change role"
        message={`Change ${name}'s role to ${toTitleCase(pendingRole ?? '')}?`}
        buttons={[
          <CustomButton
            key="cancel"
            variant="outlined"
            text="Cancel"
            onClick={cancelChangeRole}
            disabled={isLoading}
            height={35}
          />,
          <CustomButton
            key="confirm"
            variant="filled"
            color="primary"
            text="Confirm"
            onClick={confirmChangeRole}
            loading={isLoading}
            height={35}
          />,
        ]}
      />
    </>
  );
}
